import { PERMISSIONS } from "./../../../services/permissions/permissions.service";
import { Injectable, Inject } from "@angular/core";
import { AngularFireAuth } from "@angular/fire/auth";
import { AngularFireStorage } from "@angular/fire/storage";
import { AngularFirestore, DocumentData } from "@angular/fire/firestore";
import { FirebaseFirestore, FirebaseStorage } from "@angular/fire";
import { AuthService } from "../../../services/auth.service";
import { Papa } from "ngx-papaparse";

const PERMISSIONS_TYPE_IMPORTER = {
  users_by_appid: PERMISSIONS.IMPORTER_USERS_BY_APPID,
  users: PERMISSIONS.IMPORTER_USERS_BY_APPID,
  bank_transactions: PERMISSIONS.IMPORTER_BANK_TRANSACTIONS,
  transaction_credits: PERMISSIONS.IMPORTER_BANK_TRANSACTIONS,
  recognitions_points: PERMISSIONS.IMPORTER_RECOGNITIONS_POINTS,
  kpis: PERMISSIONS.IMPORTER_KPI,
  //central_deliveries_updater: PERMISSIONS.IMPORTER_CENTRAL_DELIVERIES_UPDATER,
  //central_preinvoices_updater: PERMISSIONS.IMPORTER_CENTRAL_PREINVOICES_UPDATER,
  bonos_orders_admin: PERMISSIONS.IMPORTER_ORDERS_BY_ADMIN,
  //central_order_offline: PERMISSIONS.IMPORTER_CENTRAL_ORDER_OFFLINE
};
export interface ImportsFilter {
  appId?: string;
  uid?: string;
  type?: string;
  date?: {
    start: Date;
    end: Date;
  };
  status?: string;
}

@Injectable({
  providedIn: "root",
})
export class ImportService {
  store: FirebaseStorage;
  db: FirebaseFirestore;
  lastVisible: any;

  constructor(
    @Inject("DataImportersFireAuth") private importersAuth: AngularFireAuth,
    @Inject("DataImportersFirestore")
    private importersFirestore: AngularFirestore,
    @Inject("DataImportersFireStorage")
    private importersStorage: AngularFireStorage,
    private authService: AuthService,
    private papa: Papa
  ) {}

  async importFile(file: File, ref: string) {
    return await this.store.ref(ref).put(file);
  }

  async createImportation(data: any) {
    return await this.db.collection("importers").add(data);
  }

  async readAllImports(
    limit: number,
    filter: ImportsFilter,
    allowedAppIds?: string[],
    thisAllowAllAppIds?: boolean,
    types?: string[]
  ) {
    try {
      // Obtenemos los documentos (ya vienen filtrados incluyendo los appId: null)
      const allDocs = await this.getDataFilter(
        filter,
        limit,
        types,
        allowedAppIds,
        true,
        thisAllowAllAppIds
      );

      if (!allDocs || allDocs.length === 0) return [];

      const docsProms = allDocs.map(async (item) => {
        // 'item' ya es un DocumentSnapshot, así que usamos .data() e .id
        const data = item.data();
        data.idImport = item.id;
        data.state = this.getStatus(data.state);

        // Procesamiento de CSV/Errores
        if (data.errorFileStart) {
          const errorFileStartLines = data.errorFileStart.split("\n");
          if (errorFileStartLines.length > 1) {
            const parseResult = this.papa.parse(data.errorFileStart, {
              header: false,
            });
            data.errorFileStartLines = parseResult.data
              .slice(1, 6)
              .map((r: string[]) => r.pop()?.substring(0, 100));
          }
        }

        // Obtención de URLs (Storage)
        try {
          if (data.fileHasError) {
            data.errors_url = await this.getErrorsUrl(data);
          }
        } catch (err) {
          data.errors_url = null;
        }

        try {
          if (data.file) {
            data.file = await this.store.refFromURL(data.file).getDownloadURL();
          }
        } catch (err) {
          data.file = null;
        }

        return data;
      });

      const docs = await Promise.all(docsProms);

      // Eliminamos duplicados por si acaso (aunque con una sola query no debería haber)
      return Array.from(
        new Map(docs.map((item) => [item.idImport, item])).values()
      );
    } catch (error) {
      console.error("readAllImports", error);
      return [];
    }
  }

  async getErrorsUrl(data: DocumentData) {
    const joinFile = this.store.ref(`${data.errors_prefix}-join.csv`);
    let joinFileUrl: string | null = null;
    try {
      joinFileUrl = await joinFile.getDownloadURL();
    } catch (err) {
      if (err.code !== "storage/object-not-found") {
        console.error(err);
      }
      joinFileUrl = null;
    }
    if (joinFileUrl) {
      return joinFileUrl;
    }
    const files = await this.store
      .ref(`import-errors/${data.idImport}`)
      .listAll();
    if (files.items && files.items.length) {
      return await files.items[0].getDownloadURL();
    }
    return null;
  }

  async configFirebase() {
    await this.authService.getRemoteFirebaseUser(
      this.importersAuth,
      "dataimporters"
    );
    this.store = this.importersStorage.storage;
    this.db = this.importersFirestore.firestore;
  }

  private getStatus(status: string): string {
    return status.split("_").slice(2).join("_");
  }

  private async getDataFilter(
    filter: ImportsFilter,
    limit: number,
    types?: string[],
    allowedAppIds?: string[],
    hasAppId = true,
    thisAllowAllAppIds?: boolean
  ) {
    try {
      let query: firebase.firestore.Query = this.db
        ? this.db.collection("importers").orderBy("date_time", "desc")
        : this.importersFirestore.firestore
            .collection("importers")
            .orderBy("date_time", "desc");
      let usedInOperator = false;

      // 1. GESTIÓN DE APPID (Permitidos + Null)
      if (filter?.appId) {
        query = query.where("appId", "==", filter.appId);
      } else if (!thisAllowAllAppIds) {
        if (!hasAppId) {
          query = query.where("appId", "==", null);
        } else {
          const appIdsToSearch: any[] = [...(allowedAppIds || [])];
          if (appIdsToSearch.length > 0) {
            query = query.where("appId", "in", appIdsToSearch.slice(0, 30));
            usedInOperator = true;
          }          
        }
      }

      // 2. GESTIÓN DE TIPOS (Corregido para que el filtro mande sobre el array)
      let needsManualTypeFilter = false;

      if (filter?.type) {
        // SI HAY UN FILTRO ESPECÍFICO, LO USAMOS SIEMPRE (es un ==, no rompe nada)
        query = query.where("type", "==", filter.type);
      } else if (types && types.length > 0) {
        // SI NO HAY FILTRO PERO HAY UNA LISTA DE TIPOS PERMITIDOS
        if (types.length === 1) {
          query = query.where("type", "==", types[0]);
        } else if (!usedInOperator) {
          // Solo usamos 'in' si no se usó en appId
          query = query.where("type", "in", types);
        } else {
          // Si ya hay un 'in' en appId, filtramos el array 'types' en memoria después
          needsManualTypeFilter = true;
        }
      }

      // 3. RESTO DE FILTROS
      if (filter?.uid) query = query.where("uid", "==", filter.uid);
      if (filter?.status) query = query.where("state", "==", filter.status);
      if (filter?.date?.start && filter?.date?.end) {
        query = query
          .where("date_time", ">=", filter.date.start)
          .where("date_time", "<=", filter.date.end);
      }

      const snapshot = await query.limit(limit).get();
      let docs = snapshot.docs;

      // 4. FILTRADO MANUAL (Solo si hubo colisión de múltiples tipos vs múltiples apps)
      if (needsManualTypeFilter && types) {
        docs = docs.filter((doc) => types.includes(doc.data().type));
      }

      return docs;
    } catch (error) {
      console.error("getDataFilter Error:", error);
      return [];
    }
  }
}
